import { getAuth } from 'firebase/auth';
import { addDoc, collection, getFirestore, Timestamp } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import React, { useState } from 'react';
import { FiCheckCircle, FiDollarSign, FiFileText, FiImage, FiLock, FiMusic, FiShield, FiUploadCloud } from 'react-icons/fi';
import { app } from '../firebase';
import './Upload.css';

const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

const Upload = () => {
    const [trackName, setTrackName] = useState('');
    const [audioFile, setAudioFile] = useState(null);
    const [coverFile, setCoverFile] = useState(null);
    const [coverImageUrl, setCoverImageUrl] = useState('');
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [price, setPrice] = useState('');
    const [audioProgress, setAudioProgress] = useState(0);
    const [coverProgress, setCoverProgress] = useState(0);
    const [audioDragActive, setAudioDragActive] = useState(false);
    const [coverDragActive, setCoverDragActive] = useState(false);
    const [user, setUser] = useState(() => auth.currentUser);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [audioPreview, setAudioPreview] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [uploadStage, setUploadStage] = useState(''); // 'audio', 'cover', 'saving'

    const genres = ['Hip-Hop', 'Trap', 'Drill', 'Pop', 'Electronic', 'Rock', 'Lo-Fi', 'R&B', 'Afrobeat', 'Jazz', 'Classical', 'Other'];
    
    // File size limits (in bytes)
    const MAX_AUDIO_SIZE = 50 * 1024 * 1024; // 50MB
    const MAX_COVER_SIZE = 5 * 1024 * 1024; // 5MB
    
    // Allowed file types
    const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/flac', 'audio/aac', 'audio/m4a', 'audio/mp4', 'audio/x-m4a'];
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

    const getAudioContentType = (file) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        const fallbackTypes = {
            mp3: 'audio/mpeg',
            m4a: 'audio/mp4',
            wav: 'audio/wav',
            ogg: 'audio/ogg',
            flac: 'audio/flac',
            aac: 'audio/aac',
        };

        return file.type || fallbackTypes[extension] || 'audio/mpeg';
    };

    const getImageContentType = (file) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        const fallbackTypes = {
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            webp: 'image/webp',
            gif: 'image/gif',
        };

        return file.type || fallbackTypes[extension] || 'image/jpeg';
    };

    // Listen for auth state changes
    React.useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(setUser);
        return unsubscribe;
    }, []);

    const handleAudioFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setErrorMsg('');
            
            // Check file type
            if (!selectedFile.type.startsWith('audio/') && !ALLOWED_AUDIO_TYPES.includes(selectedFile.type)) {
                setErrorMsg('Please select a valid audio file (MP3, WAV, OGG, FLAC, AAC, M4A).');
                e.target.value = '';
                return;
            }
            
            // Check file size
            if (selectedFile.size > MAX_AUDIO_SIZE) {
                setErrorMsg(`Audio file is too large. Maximum size is ${formatFileSize(MAX_AUDIO_SIZE)}.`);
                e.target.value = '';
                return;
            }
            
            setAudioFile(selectedFile);
            setAudioProgress(0);
            
            // Create preview URL
            if (audioPreview) {
                URL.revokeObjectURL(audioPreview);
            }
            const previewUrl = URL.createObjectURL(selectedFile);
            setAudioPreview(previewUrl);
        }
    };

    const handleCoverFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setErrorMsg('');
            
            // Check file type
            if (!selectedFile.type.startsWith('image/') && !ALLOWED_IMAGE_TYPES.includes(selectedFile.type)) {
                setErrorMsg('Please select a valid image file (JPEG, PNG, WebP, GIF).');
                e.target.value = '';
                return;
            }
            
            // Check file size
            if (selectedFile.size > MAX_COVER_SIZE) {
                setErrorMsg(`Cover image is too large. Maximum size is ${formatFileSize(MAX_COVER_SIZE)}.`);
                e.target.value = '';
                return;
            }
            
            setCoverFile(selectedFile);
            setCoverProgress(0);

            if (coverPreview) {
                URL.revokeObjectURL(coverPreview);
            }
            setCoverPreview(URL.createObjectURL(selectedFile));
        }
    };

    const handleAudioDragOver = (e) => {
        e.preventDefault();
        setAudioDragActive(true);
    };

    const handleAudioDragLeave = () => {
        setAudioDragActive(false);
    };

    const handleAudioDrop = (e) => {
        e.preventDefault();
        setAudioDragActive(false);
        setErrorMsg('');
        
        const droppedFile = e.dataTransfer.files[0];
        if (!droppedFile) return;
        
        // Check file type
        if (!droppedFile.type.startsWith('audio/') && !ALLOWED_AUDIO_TYPES.includes(droppedFile.type)) {
            setErrorMsg('Please drop a valid audio file (MP3, WAV, OGG, FLAC, AAC, M4A).');
            return;
        }
        
        // Check file size
        if (droppedFile.size > MAX_AUDIO_SIZE) {
            setErrorMsg(`Audio file is too large. Maximum size is ${formatFileSize(MAX_AUDIO_SIZE)}.`);
            return;
        }
        
        setAudioFile(droppedFile);
        setAudioProgress(0);
        
        // Create preview URL
        if (audioPreview) {
            URL.revokeObjectURL(audioPreview);
        }
        const previewUrl = URL.createObjectURL(droppedFile);
        setAudioPreview(previewUrl);
    };

    const handleCoverDragOver = (e) => {
        e.preventDefault();
        setCoverDragActive(true);
    };

    const handleCoverDragLeave = () => {
        setCoverDragActive(false);
    };

    const handleCoverDrop = (e) => {
        e.preventDefault();
        setCoverDragActive(false);
        setErrorMsg('');
        
        const droppedFile = e.dataTransfer.files[0];
        if (!droppedFile) return;
        
        // Check file type
        if (!droppedFile.type.startsWith('image/') && !ALLOWED_IMAGE_TYPES.includes(droppedFile.type)) {
            setErrorMsg('Please drop a valid image file (JPEG, PNG, WebP, GIF).');
            return;
        }
        
        // Check file size
        if (droppedFile.size > MAX_COVER_SIZE) {
            setErrorMsg(`Cover image is too large. Maximum size is ${formatFileSize(MAX_COVER_SIZE)}.`);
            return;
        }
        
        setCoverFile(droppedFile);
        setCoverProgress(0);

        if (coverPreview) {
            URL.revokeObjectURL(coverPreview);
        }
        setCoverPreview(URL.createObjectURL(droppedFile));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const toggleGenre = (genreName) => {
        setSelectedGenres((currentGenres) => {
            if (currentGenres.includes(genreName)) {
                return currentGenres.filter((item) => item !== genreName);
            }
            return [...currentGenres, genreName];
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMsg('');
        setErrorMsg('');
        setIsUploading(true);

        if (!user) {
            setErrorMsg('You must be logged in to upload.');
            setIsUploading(false);
            return;
        }

        if (!trackName || !audioFile) {
            setErrorMsg('Please fill in track name and upload an audio file.');
            setIsUploading(false);
            return;
        }

        try {
            // Verify user is authenticated
            if (!user || !user.uid) {
                setErrorMsg('You must be logged in to upload files.');
                setIsUploading(false);
                return;
            }

            // Verify storage is initialized
            if (!storage) {
                setErrorMsg('Storage is not initialized. Please check your Firebase configuration.');
                setIsUploading(false);
                return;
            }

            let audioUrl = '';
            let coverUrl = coverImageUrl || '';

            // Upload audio file
            setUploadStage('audio');
            const audioFileName = `${Date.now()}_${audioFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
            const storagePath = `tracks/${user.uid}/${audioFileName}`;
            
            const audioStorageRef = ref(storage, storagePath);
            const audioUploadTask = uploadBytesResumable(audioStorageRef, audioFile, {
                contentType: getAudioContentType(audioFile),
            });
            
            await new Promise((resolve, reject) => {
                audioUploadTask.on('state_changed',
                    (snapshot) => {
                        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                        setAudioProgress(progress);
                    },
                    (error) => {
                        console.error('Audio upload error:', error);
                        console.error('Error code:', error.code);
                        console.error('Error message:', error.message);
                        console.error('Error serverResponse:', error.serverResponse);
                        
                        let errorMessage = 'Failed to upload audio file. ';
                        
                        if (error.code === 'storage/unauthorized') {
                            errorMessage += 'Permission denied. Please check your Firebase Storage rules in the Firebase Console.';
                        } else if (error.code === 'storage/canceled') {
                            errorMessage += 'Upload was canceled.';
                        } else if (error.code === 'storage/quota-exceeded') {
                            errorMessage += 'Storage quota exceeded.';
                        } else if (error.code === 'storage/unauthenticated') {
                            errorMessage += 'You must be logged in to upload files.';
                        } else if (error.code === 'storage/invalid-content-type') {
                            errorMessage += 'Invalid file type. Please upload an audio file.';
                        } else if (error.message && error.message.includes('CORS')) {
                            errorMessage += 'CORS or rules error detected. Check Firebase Console → Storage → Rules and publish rules that allow signed-in users to write only inside tracks/{uid}/ and covers/{uid}/. A starter template is included in firebase/storage.rules.';
                        } else if (error.code === 'storage/object-not-found') {
                            errorMessage += 'Storage path not found. Please check your Storage rules.';
                        } else {
                            errorMessage += (error.message || 'Unknown error occurred.') + ' Check browser console for details.';
                        }
                        
                        setErrorMsg(errorMessage);
                        reject(error);
                    },
                    async () => {
                        try {
                            audioUrl = await getDownloadURL(audioUploadTask.snapshot.ref);
                            resolve();
                        } catch (err) {
                            console.error('Error getting download URL:', err);
                            reject(new Error('Failed to get audio file URL.'));
                        }
                    }
                );
            });

            // Upload cover image if provided
            if (coverFile) {
                setUploadStage('cover');
                const coverFileName = `${Date.now()}_${coverFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
                const coverStorageRef = ref(storage, `covers/${user.uid}/${coverFileName}`);
                const coverUploadTask = uploadBytesResumable(coverStorageRef, coverFile, {
                    contentType: getImageContentType(coverFile),
                });
                
                await new Promise((resolve, reject) => {
                    coverUploadTask.on('state_changed',
                (snapshot) => {
                    const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                            setCoverProgress(progress);
                },
                (error) => {
                            console.error('Cover upload error:', error);
                            let errorMessage = 'Failed to upload cover image. ';
                            
                            if (error.code === 'storage/unauthorized') {
                                errorMessage += 'Permission denied. Please check your Firebase Storage rules.';
                            } else if (error.code === 'storage/canceled') {
                                errorMessage += 'Upload was canceled.';
                            } else {
                                errorMessage += error.message || 'Unknown error occurred.';
                            }
                            
                            setErrorMsg(errorMessage);
                            reject(error);
                },
                async () => {
                            try {
                                coverUrl = await getDownloadURL(coverUploadTask.snapshot.ref);
                                resolve();
                            } catch (err) {
                                console.error('Error getting cover URL:', err);
                                reject(new Error('Failed to get cover image URL.'));
                            }
                        }
                    );
                });
            }

            // Save track metadata to Firestore
            setUploadStage('saving');
            const trackData = {
                title: trackName.trim(),
                fileUrl: audioUrl,
                        userId: user.uid,
                artist: user.displayName || user.email?.split('@')[0] || 'Unknown Artist',
                coverImage: coverUrl,
                genre: selectedGenres[0] || 'Uncategorized',
                genres: selectedGenres,
                price: price ? parseFloat(price) : 0,
                        uploadedAt: Timestamp.now(),
                createdAt: Timestamp.now(),
                isTopSeller: false,
            };
            
            await addDoc(collection(db, 'tracks'), trackData);

            setSuccessMsg(`Track "${trackName}" uploaded successfully!`);
            setUploadStage('');
            
            // Clean up preview URL
            if (audioPreview) {
                URL.revokeObjectURL(audioPreview);
                setAudioPreview(null);
            }
            if (coverPreview) {
                URL.revokeObjectURL(coverPreview);
                setCoverPreview(null);
            }
            
            // Reset form after a short delay
            setTimeout(() => {
                    setTrackName('');
                setAudioFile(null);
                setCoverFile(null);
                setCoverImageUrl('');
                    setSelectedGenres([]);
                    setPrice('');
                setAudioProgress(0);
                setCoverProgress(0);
                
                // Reset file inputs
                const audioInput = document.getElementById('audio-file');
                const coverInput = document.getElementById('cover-file');
                if (audioInput) audioInput.value = '';
                if (coverInput) coverInput.value = '';
            }, 2000);
        } catch (err) {
            console.error('Upload error:', err);
            let errorMessage = 'Upload failed. ';
            
            if (err.code === 'storage/unauthorized') {
                errorMessage += 'Permission denied. Please check your Firebase Storage rules.';
            } else if (err.code === 'storage/quota-exceeded') {
                errorMessage += 'Storage quota exceeded.';
            } else if (err.code === 'permission-denied') {
                errorMessage += 'Permission denied. Please check your Firestore rules.';
            } else {
                errorMessage += err.message || 'Unknown error occurred.';
            }
            
            setErrorMsg(errorMessage);
            setUploadStage('');
        } finally {
            setIsUploading(false);
        }
    };

    const releaseReadyCount = [trackName.trim(), audioFile, selectedGenres.length, price || price === 0, coverFile || coverImageUrl].filter(Boolean).length;
    const currentPrice = price ? `$${Number(price).toFixed(2)}` : '$0.00';

    return (
        <div className="upload-page">
            <div className="container">
                <div className="upload-header">
                    <div className="upload-kicker">
                        <FiShield />
                        <span>Producer release desk</span>
                    </div>
                    <h1 className="page-title">
                        Upload Your <span className="gradient-text">Track</span>
                    </h1>
                    <p className="page-subtitle">
                        Add the essentials, preview the release, and publish with clean marketplace terms.
                    </p>
                    <div className="upload-trust-row" aria-label="Upload benefits">
                        <span><FiLock /> Metadata protected</span>
                        <span><FiDollarSign /> Zero buyer fee positioning</span>
                        <span><FiFileText /> Clear license listing</span>
                    </div>
                </div>

                {!user && (
                    <div className="auth-warning">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        <p>You must be logged in to upload tracks.</p>
                    </div>
                )}

                {errorMsg && (
                    <div className="message error-message">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <span>{errorMsg}</span>
                    </div>
                )}

                {successMsg && (
                    <div className="message success-message">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        <span>{successMsg}</span>
                    </div>
                )}

                <div className="upload-workspace">
            <form className="upload-form" onSubmit={handleSubmit}>
                    <div className="form-section-heading">
                        <span className="section-step">1</span>
                        <div>
                            <h2>Release details</h2>
                            <p>Name the beat and set the basic marketplace filters.</p>
                        </div>
                    </div>
                    {/* Track Name */}
                    <div className="form-group">
                        <label htmlFor="track-name" className="form-label">
                            Track Name <span className="required">*</span>
                        </label>
                <input
                    type="text"
                    id="track-name"
                    value={trackName}
                    onChange={(e) => setTrackName(e.target.value)}
                            placeholder="Enter your track name"
                    required
                            className="form-input"
                        />
                    </div>

                    {/* Genres */}
                    <div className="form-group">
                        <fieldset className="genre-fieldset">
                            <legend className="form-label">Genres</legend>
                            <div className="genre-picker" aria-label="Select one or more genres">
                                {genres.map((g) => (
                                    <button
                                        key={g}
                                        type="button"
                                        className={`genre-choice ${selectedGenres.includes(g) ? 'selected' : ''}`}
                                        onClick={() => toggleGenre(g)}
                                        aria-pressed={selectedGenres.includes(g)}
                                    >
                                        {g}
                                    </button>
                                ))}
                            </div>
                            <p className="form-hint">Pick every lane this beat fits. The first selected genre becomes the primary marketplace tag.</p>
                        </fieldset>
                    </div>

                    {/* Price */}
                    <div className="form-group">
                        <label htmlFor="price" className="form-label">Price (USD)</label>
                        <div className="price-input-wrapper">
                            <span className="price-symbol">$</span>
                <input
                    type="number"
                    id="price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                                placeholder="0.00"
                    min="0"
                    step="0.01"
                                className="form-input price-input"
                            />
                        </div>
                        <p className="form-hint">Leave blank or set to 0 for free tracks</p>
                    </div>

                    <div className="form-section-heading">
                        <span className="section-step">2</span>
                        <div>
                            <h2>Beat files</h2>
                            <p>Upload the audio and artwork buyers will see first.</p>
                        </div>
                    </div>

                    {/* Audio File Upload */}
                    <div className="form-group">
                    <label className="form-label" htmlFor="audio-file">
                            Audio File <span className="required">*</span>
                        </label>
                        <div
                            className={`drop-zone audio-drop-zone ${audioDragActive ? 'active' : ''} ${audioFile ? 'has-file' : ''}`}
                            onDragOver={handleAudioDragOver}
                            onDragLeave={handleAudioDragLeave}
                            onDrop={handleAudioDrop}
                        >
                    <input
                        type="file"
                                id="audio-file"
                        accept="audio/*"
                                onChange={handleAudioFileChange}
                        required
                                className="file-input"
                            />
                            <div className="drop-zone-content">
                                <FiUploadCloud className="drop-zone-icon" />
                                {audioFile ? (
                                    <div className="file-info">
                                        <p className="file-name">{audioFile.name}</p>
                                        <p className="file-size">{formatFileSize(audioFile.size)}</p>
                                    </div>
                                ) : (
                                    <div className="drop-zone-text">
                                        <p className="drop-zone-title">Drag & drop your audio file</p>
                                        <p className="drop-zone-subtitle">or click to browse</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        {audioFile && (
                            <div className="file-preview-section">
                                <div className="audio-preview">
                                    {audioPreview && (
                                        <audio controls src={audioPreview} className="audio-player-preview">
                                            Your browser does not support the audio element.
                                        </audio>
                                    )}
                                </div>
                            </div>
                        )}
                        {audioProgress > 0 && audioProgress < 100 && (
                            <div className="progress-container">
                                <div className="progress-info">
                                    <span className="progress-label">
                                        {uploadStage === 'audio' ? 'Uploading audio...' : 'Processing...'}
                                    </span>
                                </div>
                                <div className="progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${audioProgress}%` }}
                                    ></div>
                                </div>
                                <span className="progress-text">{audioProgress}%</span>
                            </div>
                        )}
                        {audioProgress === 100 && uploadStage === 'audio' && (
                            <div className="progress-complete">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                    <polyline points="22 4 12 14.01 9 11.01"/>
                                </svg>
                                Audio uploaded successfully!
                </div>
                        )}
                    </div>

                    {/* Cover Image Upload */}
                    <div className="form-group">
                    <label className="form-label" htmlFor="cover-file">Cover Art</label>
                        <div
                            className={`drop-zone cover-drop-zone ${coverDragActive ? 'active' : ''} ${coverFile ? 'has-file' : ''}`}
                            onDragOver={handleCoverDragOver}
                            onDragLeave={handleCoverDragLeave}
                            onDrop={handleCoverDrop}
                        >
                            <input
                                type="file"
                                id="cover-file"
                                accept="image/*"
                                onChange={handleCoverFileChange}
                                className="file-input"
                            />
                            <div className="drop-zone-content">
                                {coverFile ? (
                                    <>
                                        <img
                                            src={coverPreview}
                                            alt="Cover preview" 
                                            className="cover-preview"
                                        />
                                        <div className="file-info">
                                            <p className="file-name">{coverFile.name}</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <FiImage className="drop-zone-icon" />
                                        <div className="drop-zone-text">
                                            <p className="drop-zone-title">Drag & drop cover image</p>
                                            <p className="drop-zone-subtitle">or click to browse</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        {coverProgress > 0 && coverProgress < 100 && (
                            <div className="progress-container">
                                <div className="progress-info">
                                    <span className="progress-label">
                                        {uploadStage === 'cover' ? 'Uploading cover...' : 'Processing...'}
                                    </span>
                                </div>
                                <div className="progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${coverProgress}%` }}
                        ></div>
                                </div>
                                <span className="progress-text">{coverProgress}%</span>
                            </div>
                        )}
                        {coverProgress === 100 && uploadStage === 'cover' && (
                            <div className="progress-complete">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                    <polyline points="22 4 12 14.01 9 11.01"/>
                                </svg>
                                Cover uploaded successfully!
                            </div>
                        )}
                        {uploadStage === 'saving' && (
                            <div className="progress-container">
                                <div className="progress-info">
                                    <span className="progress-label">Saving track information...</span>
                                </div>
                                <div className="progress-bar">
                                    <div className="progress-fill indeterminate"></div>
                                </div>
                            </div>
                        )}
                        <div className="or-divider">
                            <span>OR</span>
                        </div>
                        <input
                            type="text"
                            value={coverImageUrl}
                            onChange={(e) => setCoverImageUrl(e.target.value)}
                            placeholder="Paste cover image URL"
                            className="form-input"
                        />
                    </div>

                    {/* Submit Button */}
                    <button
                    type="submit"
                        className="submit-button"
                        disabled={isUploading || !user || !trackName.trim() || !audioFile}
                    >
                        {isUploading ? (
                            <>
                                <div className="button-spinner"></div>
                                <span>
                                    {uploadStage === 'audio' && 'Uploading audio...'}
                                    {uploadStage === 'cover' && 'Uploading cover...'}
                                    {uploadStage === 'saving' && 'Saving track...'}
                                    {!uploadStage && 'Uploading...'}
                                </span>
                            </>
                        ) : (
                            <>
                                <FiUploadCloud />
                                <span>Upload Track</span>
                            </>
                        )}
                </button>
            </form>

                    <aside className="upload-side-panel" aria-label="Release preview">
                        <div className="release-preview-card">
                            <div className="release-artwork">
                                {coverPreview || coverImageUrl ? (
                                    <img src={coverPreview || coverImageUrl} alt="" />
                                ) : (
                                    <FiMusic />
                                )}
                            </div>
                            <div className="release-preview-copy">
                                <span>Marketplace preview</span>
                                <h2>{trackName.trim() || 'Untitled beat'}</h2>
                                <p>{user?.displayName || user?.email?.split('@')[0] || 'Your producer name'}</p>
                            </div>
                            <div className="release-meta-grid">
                                <div>
                                    <span>Genres</span>
                                    <strong>{selectedGenres.length ? selectedGenres.join(', ') : 'Not set'}</strong>
                                </div>
                                <div>
                                    <span>License</span>
                                    <strong>{currentPrice}</strong>
                                </div>
                            </div>
                        </div>

                        <div className="publish-checklist-card">
                            <div className="side-card-heading">
                                <h3>Publish checklist</h3>
                                <span>{releaseReadyCount}/5 ready</span>
                            </div>
                            <ul>
                                <li className={trackName.trim() ? 'complete' : ''}><FiCheckCircle /> Track title</li>
                                <li className={audioFile ? 'complete' : ''}><FiCheckCircle /> Audio file</li>
                                <li className={selectedGenres.length ? 'complete' : ''}><FiCheckCircle /> Genre tags</li>
                                <li className={(price || price === 0) ? 'complete' : ''}><FiCheckCircle /> Price</li>
                                <li className={(coverFile || coverImageUrl) ? 'complete' : ''}><FiCheckCircle /> Cover art</li>
                            </ul>
                        </div>

                        <div className="license-note-card">
                            <FiFileText />
                            <div>
                                <h3>Buyer-friendly listing</h3>
                                <p>Show the price up front and keep the checkout promise clean. No surprise buyer fees.</p>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default Upload;
